import posthog from "posthog-js";
import { type UserProfile } from "~/schemas/user";
import * as Sentry from "@sentry/react";

export function initPosthog() {
  // Only initialize on client side
  if (typeof window === "undefined") return null;
  
  return posthog.init(
    "phc_9DzSkO2gtvx77qhyRZqwPKO9hOonDrc4J90dgXPSzBk",
    {
      api_host: "https://eu.i.posthog.com",
      debug: import.meta.env.DEV,
      defaults: "2025-05-24",
      enable_heatmaps: true,
    },
    "mina"
  );
}

// Get PostHog instance safely
function getPosthog() {
  return window.mina?.posthog;
}

function track(eventName: string, properties?: Record<string, any>) {
  const ph = getPosthog();
  if (!ph) return; // Analytics not ready yet, silently skip
  
  properties = { ...properties };
  properties["env"] = import.meta.env.PROD ? "prod" : "dev";
  try {
    ph.capture(eventName, properties);
  } catch (error) {
    Sentry.captureException(error);
  }
}

function maybeAddEmailToProps(
  props: Record<string, any>,
  user: UserProfile | null
) {
  if (user) {
    if (user.is_anon) {
      props = { ...props, anon_email: user.email };
    } else {
      props = { ...props, email: user.email };
    }
  }
  return props || {};
}

export function identify(user: UserProfile) {
  const ph = getPosthog();
  if (!ph) return; // Analytics not ready yet, silently skip
  
  try {
    const distinct_id = ph.get_distinct_id();
    if (distinct_id.startsWith("anon_user_set_by_mana")) {
      // we changed the identify flow, so this is to reset the identified devices using
      // the previous distinct ID that used the user email
      reset();
    }
    if (user.is_anon) return;
    ph.identify(user.email, { email: user.email });
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function reset() {
  const ph = getPosthog();
  if (!ph) return; // Analytics not ready yet, silently skip
  
  try {
    ph.reset();
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function trackReportFormClicked() {
  track("Report Form Clicked");
}

export function trackSignIn(user: UserProfile) {
  const props = maybeAddEmailToProps({}, user);
  track("User Signed In", props);
}

export function trackSignOut(user: UserProfile | null) {
  const props = maybeAddEmailToProps({}, user);
  track("User Signed Out", props);
}

export function trackChatSessionStarted(
  user: UserProfile | null,
  session_id: string
) {
  const props = maybeAddEmailToProps({ session_id: session_id }, user);
  track("Chat Session Started", props);
}

export function trackQuestionAsked(
  user: UserProfile | null,
  session_id: string
) {
  const props = maybeAddEmailToProps({ session_id: session_id }, user);
  track("Chat Question Asked", props);
}

export function trackSessionRated(
  user: UserProfile | null,
  session_id: string,
  vote: string
) {
  const props = maybeAddEmailToProps({ session_id: session_id, vote }, user);
  track("Chat Session Rated", props);
}
